import { toZonedDateTime, ZonedDateTime } from '@spa-frontend/date-lib';
import React, { Component, ErrorInfo, ReactNode } from 'react';

import { FlexBox } from 'ml-rubiks-styleguide/src/box';

import { usePaymentErrorContext } from '../../contexts/payment-error-context';
import {
    reportRuntimeError,
    type WidgetTelemetryContext
} from '../../core/logs';
import { PaymentError } from '../../core/payment-error';
import { UnifiedError } from './unified-error';

const CHUNK_RELOAD_SESSION_KEY = 'spa-payment-widget:chunk-reload-at';
const CHUNK_RELOAD_GUARD_SECONDS = 5 * 60;

const isChunkLoadError = (error: Error) =>
    error.name === 'ChunkLoadError' ||
    /Loading chunk \S+ failed/.test(error.message);

const reloadForStaleChunk = () => {
    try {
        const lastStr = sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY);
        const last = lastStr ? toZonedDateTime(lastStr, '') : undefined;
        if (
            last &&
            ZonedDateTime.now('').diff(last, 'second') <
                CHUNK_RELOAD_GUARD_SECONDS
        ) {
            return false;
        }
        sessionStorage.setItem(
            CHUNK_RELOAD_SESSION_KEY,
            ZonedDateTime.now('').toString()
        );
    } catch {
        // Without storage we cannot guard against an infinite reload loop;
        // surface the error instead. The user can refresh manually.
        return false;
    }
    window.location.reload();
    return true;
};

interface Props {
    children: ReactNode;
    fallback?: React.ComponentType<{ error: Error; onRetry: () => void }>;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
    onRetry?: () => void;
    errorContext?: ReturnType<typeof usePaymentErrorContext>;
    onPaymentError?: (error: PaymentError) => void;
    telemetryContext?: WidgetTelemetryContext;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        const paymentError = PaymentError.fromUnknown(error);
        reportRuntimeError(
            error,
            paymentError,
            errorInfo,
            this.props.telemetryContext
        );

        if (isChunkLoadError(error) && reloadForStaleChunk()) {
            return;
        }

        if (this.props.errorContext) {
            this.props.errorContext.addError(paymentError);
            this.props.errorContext.activateErrorBoundary();
        } else {
            this.props.onPaymentError?.(paymentError);
        }

        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }
    }

    private handleRetry = () => {
        this.setState({ hasError: false, error: null });

        if (this.props.errorContext) {
            this.props.errorContext.deactivateErrorBoundary();
            this.props.errorContext.clearErrors();
        }

        if (this.props.onRetry) {
            this.props.onRetry();
        }
    };

    public render() {
        if (this.state.hasError && this.state.error) {
            if (this.props.errorContext || this.props.onPaymentError) {
                return null;
            }

            if (this.props.fallback) {
                const FallbackComponent = this.props.fallback;
                return (
                    <FallbackComponent
                        error={this.state.error}
                        onRetry={this.handleRetry}
                    />
                );
            }

            return (
                <FlexBox sx={{ mx: 'auto', p: 3 }}>
                    <UnifiedError
                        message="An unexpected error occurred in the payment widget"
                        error={this.state.error}
                    />
                </FlexBox>
            );
        }

        return this.props.children;
    }
}

export const ErrorBoundaryWithContext: React.FC<Omit<Props, 'errorContext'>> = (
    props
) => {
    try {
        const errorContext = usePaymentErrorContext();
        return <ErrorBoundary {...props} errorContext={errorContext} />;
    } catch {
        // If PaymentErrorContext is not available, use ErrorBoundary without context
        return <ErrorBoundary {...props} />;
    }
};

export function withErrorBoundary<P extends object>(
    Component: React.ComponentType<P>,
    errorBoundaryProps?: Omit<Props, 'children' | 'errorContext'>
) {
    const WrappedComponent = (props: P) => (
        <ErrorBoundaryWithContext {...errorBoundaryProps}>
            <Component {...props} />
        </ErrorBoundaryWithContext>
    );

    WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
    return WrappedComponent;
}
